import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { submitRating } from './bookingService';
import LoadingSpinner from './components/LoadingSpinner';

export default function RateTeacher() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = searchParams.get('bookingId');

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [tags, setTags] = useState([]);

  const availableTags = [
    'Knowledgeable',
    'Patient',
    'Clear Explanations',
    'Engaging',
    'Well Prepared',
    'Helpful',
    'Professional',
    'Friendly'
  ];

  useEffect(() => {
    if (!bookingId) {
      setError('No booking ID provided');
      setLoading(false);
      return;
    }
    fetchBooking();
  }, [bookingId]);

  async function fetchBooking() {
    try {
      // Check authentication FIRST before making any queries
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        // Redirect to sign-in with return URL
        const returnUrl = encodeURIComponent(`/dashboard/student/rate?bookingId=${bookingId}`);
        navigate(`/signin?returnTo=${returnUrl}`);
        return;
      }

      // Now safely query with authenticated user
      const { data, error } = await supabase
        .from('booking_requests')
        .select('*')
        .eq('id', bookingId)
        .eq('student_id', user.id)
        .single();

      if (error) {
        console.error('Supabase error:', error);

        // Handle specific error cases
        if (error.code === 'PGRST116') {
          // No rows returned - booking not found or doesn't belong to user
          setError('Booking not found or you do not have access to rate this session');
        } else {
          setError('Failed to load booking details. Please try again.');
        }
        setLoading(false);
        return;
      }

      if (!data) {
        setError('Booking not found');
        setLoading(false);
        return;
      }

      // Check if already rated
      const { data: existingRating } = await supabase
        .from('ratings')
        .select('*')
        .eq('booking_id', bookingId)
        .maybeSingle();

      if (existingRating) {
        setError('You have already rated this session');
        setLoading(false);
        return;
      }

      setBooking(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError('Failed to load booking details. Please try again.');
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/signin');
        return;
      }

      await submitRating({
        bookingId,
        studentId: user.id,
        studentEmail: user.email,
        teacherId: booking.teacher_uuid || booking.teacher_id || null,
        teacherEmail: booking.teacher_email,
        rating,
        comment: comment.trim(),
        tags
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard/student/requests');
      }, 2000);
    } catch (err) {
      console.error('Error submitting rating:', err);
      setError(err.message || 'Failed to submit rating');
      setSubmitting(false);
    }
  }

  function toggleTag(tag) {
    setTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f7dac1',
        padding: '60px 20px'
      }}>
        <div style={{
          maxWidth: 700,
          margin: '0 auto',
          background: 'white',
          padding: 60,
          borderRadius: 8,
          border: '1px solid #e5e5e5',
          textAlign: 'center'
        }}>
          <LoadingSpinner size={70} />
          <p style={{ color: '#666', fontSize: 15, marginTop: 20 }}>Loading session details...</p>
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f7dac1',
        padding: '60px 20px'
      }}>
        <div style={{
          maxWidth: 500,
          margin: '0 auto',
          background: 'white',
          padding: 60,
          borderRadius: 8,
          border: '1px solid #e5e5e5',
          textAlign: 'center'
        }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: '#FEF2F2',
            border: '2px solid #FCA5A5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: 36,
            color: '#DC2626'
          }}>
            ✕
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12, color: '#1E293B' }}>
            {error}
          </h2>
          <p style={{ color: '#64748B', fontSize: 15, marginBottom: 32 }}>
            Unable to load this session. Please try again or contact support.
          </p>
          <button
            onClick={() => navigate('/dashboard/student/requests')}
            style={{
              background: '#1E293B',
              color: 'white',
              border: 'none',
              padding: '14px 32px',
              borderRadius: 6,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Back to My Sessions
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f7dac1',
        padding: '60px 20px'
      }}>
        <div style={{
          maxWidth: 500,
          margin: '0 auto',
          background: 'white',
          padding: 60,
          borderRadius: 8,
          border: '1px solid #e5e5e5',
          textAlign: 'center'
        }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: '#ECFDF5',
            border: '2px solid #6EE7B7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: 36,
            color: '#059669'
          }}>
            ✓
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12, color: '#1E293B' }}>
            Thank You!
          </h2>
          <p style={{ color: '#64748B', fontSize: 15 }}>
            Your feedback helps us improve. Redirecting...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f7dac1',
      padding: '40px 20px 80px'
    }}>
      <div style={{
        maxWidth: 700,
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          background: 'white',
          padding: '32px 40px',
          borderRadius: '8px 8px 0 0',
          border: '1px solid #e5e5e5',
          borderBottom: '2px solid #FF7426'
        }}>
          <h1 style={{
            fontSize: 26,
            fontWeight: 700,
            margin: 0,
            color: '#1E293B',
            marginBottom: 8
          }}>
            Rate Your Session
          </h1>
          <p style={{
            color: '#64748B',
            fontSize: 15,
            margin: 0
          }}>
            Session with <strong>{booking?.teacher_name}</strong> on {booking?.date}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{
          background: 'white',
          padding: '40px',
          borderRadius: '0 0 8px 8px',
          border: '1px solid #e5e5e5',
          borderTop: 'none'
        }}>
          {/* Star Rating */}
          <div style={{ marginBottom: 36 }}>
            <label style={{
              display: 'block',
              fontSize: 15,
              fontWeight: 600,
              color: '#334155',
              marginBottom: 16
            }}>
              How would you rate this session?
            </label>
            <div style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              padding: '20px 0'
            }}>
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 42,
                    padding: 8,
                    transition: 'transform 0.2s',
                    transform: (hoveredRating >= star || rating >= star) ? 'scale(1.15)' : 'scale(1)',
                    color: (hoveredRating >= star || rating >= star) ? '#FF7426' : '#CBD5E1'
                  }}
                >
                  {hoveredRating >= star || rating >= star ? '★' : '☆'}
                </button>
              ))}
            </div>
            <div style={{
              textAlign: 'center',
              color: '#64748B',
              fontSize: 14,
              marginTop: 8
            }}>
              {rating === 0 ? 'Select a rating' :
               rating === 1 ? 'Poor' :
               rating === 2 ? 'Fair' :
               rating === 3 ? 'Good' :
               rating === 4 ? 'Very Good' :
               'Excellent'}
            </div>
          </div>

          {/* Divider */}
          <div style={{
            height: 1,
            background: '#E5E7EB',
            margin: '32px 0'
          }} />

          {/* Tags */}
          <div style={{ marginBottom: 32 }}>
            <label style={{
              display: 'block',
              fontSize: 15,
              fontWeight: 600,
              color: '#334155',
              marginBottom: 16
            }}>
              What did you like? <span style={{ color: '#94A3B8', fontWeight: 400 }}>(Optional)</span>
            </label>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10
            }}>
              {availableTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  style={{
                    background: tags.includes(tag) ? '#FFF4ED' : 'white',
                    color: tags.includes(tag) ? '#FF7426' : '#64748B',
                    border: tags.includes(tag) ? '2px solid #FF7426' : '2px solid #E5E7EB',
                    padding: '10px 20px',
                    borderRadius: 6,
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div style={{ marginBottom: 32 }}>
            <label style={{
              display: 'block',
              fontSize: 15,
              fontWeight: 600,
              color: '#334155',
              marginBottom: 12
            }}>
              Additional Comments <span style={{ color: '#94A3B8', fontWeight: 400 }}>(Optional)</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell us more about your experience..."
              rows={4}
              style={{
                width: '100%',
                padding: 14,
                borderRadius: 6,
                border: '2px solid #E5E7EB',
                fontSize: 14,
                resize: 'vertical',
                fontFamily: 'inherit',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#FF7426'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>

          {error && (
            <div style={{
              background: '#FEF2F2',
              border: '1px solid #FCA5A5',
              color: '#991B1B',
              padding: 14,
              borderRadius: 6,
              fontSize: 14,
              marginBottom: 24
            }}>
              {error}
            </div>
          )}

          {/* Submit Button */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              type="button"
              onClick={() => navigate('/dashboard/student/requests')}
              style={{
                flex: 1,
                background: 'white',
                color: '#64748B',
                border: '2px solid #E5E7EB',
                padding: '14px 24px',
                borderRadius: 6,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || rating === 0}
              style={{
                flex: 2,
                background: rating === 0 ? '#E5E7EB' : '#FF7426',
                color: 'white',
                border: 'none',
                padding: '14px 24px',
                borderRadius: 6,
                fontSize: 15,
                fontWeight: 600,
                cursor: rating === 0 ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: rating === 0 ? 0.6 : 1
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Rating'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
